import { WatchmanFetch } from '../../../shared/utils/watchmanFetch';
import type {
  CreateDepartmentPayload,
  CreateDepartmentRolePayload,
  CreateStaffAssignmentPayload,
  Department,
  DepartmentRole,
  RolePermissionEntry,
  StaffAssignment,
  UpdateDepartmentPayload,
  UpdateDepartmentRolePayload,
} from '../types';

/**
 * Watchman departments domain via osprey-api's `/iam/*` proxy (PE-2147).
 * Watchman returns Prisma rows directly (no `{ data }` envelope), so nothing
 * here needs `unwrap`.
 */
export const departmentsApi = {
  list: (): Promise<Department[]> => WatchmanFetch.get('/departments'),
  get: (id: string): Promise<Department> => WatchmanFetch.get(`/departments/${id}`),
  /** 409 when the (unique) name already exists. */
  create: (payload: CreateDepartmentPayload): Promise<Department> =>
    WatchmanFetch.post('/departments', payload),
  update: (id: string, payload: UpdateDepartmentPayload): Promise<Department> =>
    WatchmanFetch.patch(`/departments/${id}`, payload),
  deactivate: (id: string): Promise<Department> => WatchmanFetch.patch(`/departments/${id}/deactivate`),
  activate: (id: string): Promise<Department> => WatchmanFetch.patch(`/departments/${id}/activate`),
};

export const departmentRolesApi = {
  list: (departmentId?: string): Promise<DepartmentRole[]> =>
    WatchmanFetch.get(`/department-roles${departmentId ? `?departmentId=${encodeURIComponent(departmentId)}` : ''}`),
  get: (id: string): Promise<DepartmentRole> => WatchmanFetch.get(`/department-roles/${id}`),
  create: (payload: CreateDepartmentRolePayload): Promise<DepartmentRole> =>
    WatchmanFetch.post('/department-roles', payload),
  update: (id: string, payload: UpdateDepartmentRolePayload): Promise<DepartmentRole> =>
    WatchmanFetch.patch(`/department-roles/${id}`, payload),
  deactivate: (id: string): Promise<DepartmentRole> =>
    WatchmanFetch.patch(`/department-roles/${id}/deactivate`),
  activate: (id: string): Promise<DepartmentRole> => WatchmanFetch.patch(`/department-roles/${id}/activate`),
  listPermissions: (id: string): Promise<RolePermissionEntry[]> =>
    WatchmanFetch.get(`/department-roles/${id}/permissions`),
  /** Full replace — the server drops every row not in the payload. */
  setPermissions: (id: string, permissions: RolePermissionEntry[]): Promise<RolePermissionEntry[]> =>
    WatchmanFetch.put(`/department-roles/${id}/permissions`, { permissions }),
};

export const departmentAssignmentsApi = {
  /** The caller's own assignments — session only, no DEPARTMENT permission needed. */
  me: (): Promise<StaffAssignment[]> => WatchmanFetch.get('/department-assignments/me'),
  list: (query: { userId?: string; departmentId?: string } = {}): Promise<StaffAssignment[]> => {
    const search = new URLSearchParams();
    if (query.userId) search.append('userId', query.userId);
    if (query.departmentId) search.append('departmentId', query.departmentId);
    const qs = search.toString();
    return WatchmanFetch.get(`/department-assignments${qs ? `?${qs}` : ''}`);
  },
  /** 400 when the user is not COLLECTIVE-scope; 409 when they already hold that role. */
  create: (payload: CreateStaffAssignmentPayload): Promise<StaffAssignment> =>
    WatchmanFetch.post('/department-assignments', payload),
  remove: (id: string): Promise<{ deleted: boolean }> => WatchmanFetch.del(`/department-assignments/${id}`),
};
